import { useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { ZoomIn, ZoomOut, Maximize, Plus, Trash2, Eye, Save } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import ConceptMap from './ConceptMap';

let counter = 0;
const nid = () => `n_${Date.now().toString(36)}_${(counter++).toString(36)}`;

function ensureIds(node) {
  if (!node.id) node.id = nid();
  (node.children || []).forEach(ensureIds);
  return node;
}

function flatten(root) {
  const out = [];
  const walk = (n, parentId, depth) => {
    out.push({ node: n, parentId, depth });
    (n.children || []).forEach((c) => walk(c, n.id, depth + 1));
  };
  walk(root, null, 0);
  return out;
}

function autoLayout(root, layout) {
  const pos = { ...(layout || {}) };
  const perLevel = {};
  flatten(root).forEach(({ node, depth }) => {
    perLevel[depth] = perLevel[depth] || 0;
    if (!pos[node.id]) pos[node.id] = { x: perLevel[depth] * 200, y: depth * 110 };
    perLevel[depth] += 1;
  });
  return pos;
}

function removeNode(node, id) {
  node.children = (node.children || []).filter((c) => c.id !== id);
  node.children.forEach((c) => removeNode(c, id));
}

function findNode(node, id) {
  if (node.id === id) return node;
  for (const c of node.children || []) {
    const f = findNode(c, id);
    if (f) return f;
  }
  return null;
}

const NODE_W = 150;
const NODE_H = 44;

export default function MapEditor({ data, onSave }) {
  const [tree, setTree] = useState(() => ensureIds(JSON.parse(JSON.stringify(data?.root || { label: 'Mappa', children: [] }))));
  const [title, setTitle] = useState(data?.title || 'Mappa concettuale');
  const [pos, setPos] = useState(() => autoLayout(ensureIds(JSON.parse(JSON.stringify(data?.root || { label: 'Mappa', children: [] }))), data?.layout));
  const [selected, setSelected] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 20, y: 20 });
  const [preview, setPreview] = useState(false);
  const dragRef = useRef(null);
  const canvasRef = useRef(null);

  const items = useMemo(() => flatten(tree), [tree]);
  const selectedNode = selected ? findNode(tree, selected) : null;

  const onPointerDownNode = (e, id) => {
    e.stopPropagation();
    setSelected(id);
    const p = pos[id];
    dragRef.current = { type: 'node', id, startX: e.clientX, startY: e.clientY, origX: p.x, origY: p.y };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };

  const onPointerDownBg = (e) => {
    if (e.target !== canvasRef.current) return;
    dragRef.current = { type: 'pan', startX: e.clientX, startY: e.clientY, origX: pan.x, origY: pan.y };
  };

  const onPointerMove = (e) => {
    const d = dragRef.current;
    if (!d) return;
    const dx = (e.clientX - d.startX) / zoom;
    const dy = (e.clientY - d.startY) / zoom;
    if (d.type === 'node') {
      setPos((p) => ({ ...p, [d.id]: { x: Math.round(d.origX + dx), y: Math.round(d.origY + dy) } }));
    } else {
      setPan({ x: d.origX + e.clientX - d.startX, y: d.origY + e.clientY - d.startY });
    }
  };

  const onPointerUp = () => { dragRef.current = null; };

  const addChild = () => {
    if (!selected) { toast.info('Seleziona prima un nodo'); return; }
    const t = JSON.parse(JSON.stringify(tree));
    const parent = findNode(t, selected);
    const child = { id: nid(), label: 'Nuovo nodo', children: [] };
    parent.children = parent.children || [];
    parent.children.push(child);
    const pp = pos[selected];
    setPos((p) => ({ ...p, [child.id]: { x: pp.x + 40 + parent.children.length * 30, y: pp.y + 110 } }));
    setTree(t);
    setSelected(child.id);
  };

  const deleteNode = () => {
    if (!selected || selected === tree.id) { toast.info('Il nodo radice non si può eliminare'); return; }
    if (!window.confirm('Eliminare il nodo e tutti i suoi discendenti?')) return;
    const t = JSON.parse(JSON.stringify(tree));
    removeNode(t, selected);
    setTree(t);
    setSelected(null);
  };

  const rename = (label) => {
    const t = JSON.parse(JSON.stringify(tree));
    findNode(t, selected).label = label;
    setTree(t);
  };

  const relayout = () => { setPos(autoLayout(tree, {})); setPan({ x: 20, y: 20 }); setZoom(1); };

  const save = () => {
    onSave({ title, root: tree, layout: pos });
    toast.success('Mappa salvata');
  };

  if (preview) {
    return (
      <div data-testid="map-editor-preview">
        <div className="flex justify-between items-center mb-4">
          <p className="text-sm font-semibold text-slate-700">Anteprima reader</p>
          <Button variant="outline" size="sm" onClick={() => setPreview(false)} data-testid="map-close-preview">Torna all'editor</Button>
        </div>
        <ConceptMap data={{ title, root: tree }} testId="map-preview" />
      </div>
    );
  }

  return (
    <div data-testid="map-editor">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <Input value={title} onChange={(e) => setTitle(e.target.value)} className="w-56 h-9" placeholder="Titolo mappa" data-testid="map-title" />
        <Button type="button" variant="outline" size="sm" onClick={addChild} data-testid="map-add-child"><Plus size={14} className="mr-1" /> Figlio</Button>
        <Button type="button" variant="outline" size="sm" onClick={deleteNode} data-testid="map-delete"><Trash2 size={14} className="mr-1" /> Elimina</Button>
        <Button type="button" variant="outline" size="sm" onClick={() => setZoom((z) => Math.min(2, z + 0.15))}><ZoomIn size={14} /></Button>
        <Button type="button" variant="outline" size="sm" onClick={() => setZoom((z) => Math.max(0.4, z - 0.15))}><ZoomOut size={14} /></Button>
        <Button type="button" variant="outline" size="sm" onClick={relayout} data-testid="map-relayout"><Maximize size={14} className="mr-1" /> Riordina</Button>
        <Button type="button" variant="outline" size="sm" onClick={() => setPreview(true)} data-testid="map-preview-btn"><Eye size={14} className="mr-1" /> Anteprima</Button>
        <Button type="button" size="sm" onClick={save} className="bg-brand hover:bg-brand-hover text-white" data-testid="map-save"><Save size={14} className="mr-1" /> Salva mappa</Button>
      </div>
      {selectedNode && (
        <div className="flex items-center gap-2 mb-3">
          <span className="text-xs text-slate-500 uppercase tracking-wide font-semibold">Nodo selezionato:</span>
          <Input value={selectedNode.label} onChange={(e) => rename(e.target.value)} className="h-9 max-w-xs" data-testid="map-rename" />
        </div>
      )}
      <div
        ref={canvasRef}
        data-testid="map-canvas"
        className="relative h-[420px] border border-slate-200 rounded-xl bg-slate-50 overflow-hidden touch-none select-none cursor-grab active:cursor-grabbing"
        onPointerDown={onPointerDownBg}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerLeave={onPointerUp}
        onWheel={(e) => setZoom((z) => Math.min(2, Math.max(0.4, z - Math.sign(e.deltaY) * 0.1)))}
      >
        <div style={{ transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`, transformOrigin: '0 0' }} className="absolute inset-0">
          <svg className="absolute overflow-visible" width="1" height="1">
            {items.filter((i) => i.parentId).map((i) => {
              const a = pos[i.parentId];
              const b = pos[i.node.id];
              if (!a || !b) return null;
              return (
                <line key={i.node.id}
                  x1={a.x + NODE_W / 2} y1={a.y + NODE_H} x2={b.x + NODE_W / 2} y2={b.y}
                  stroke="#94A3B8" strokeWidth="1.5" />
              );
            })}
          </svg>
          {items.map(({ node, depth }) => {
            const p = pos[node.id] || { x: 0, y: 0 };
            const isSel = node.id === selected;
            return (
              <div
                key={node.id}
                data-testid={`map-node-${node.id}`}
                onPointerDown={(e) => onPointerDownNode(e, node.id)}
                className={`absolute flex items-center justify-center text-center px-3 py-2 rounded-xl border text-sm font-medium cursor-move transition-colors ${
                  depth === 0 ? 'bg-brand text-white border-brand' : 'bg-white text-slate-800 border-slate-300'
                } ${isSel ? 'ring-2 ring-gold ring-offset-1' : ''}`}
                style={{ left: p.x, top: p.y, width: NODE_W, minHeight: NODE_H }}
              >
                <span className="line-clamp-2 text-xs sm:text-sm">{node.label}</span>
              </div>
            );
          })}
        </div>
      </div>
      <p className="text-xs text-slate-400 mt-2">Trascina i nodi per riposizionarli, trascina lo sfondo per spostare la vista, rotellina per lo zoom. Le gerarchie padre/figlio sono salvate come dati strutturati.</p>
    </div>
  );
}
